import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, Info, ArrowLeft } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PageHeader, { EmptyState } from "@/components/PageHeader";
import { LetterPolicyCard } from "@/components/LetterPolicyCard";

export default function CoachAchievements() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [canCreate, setCanCreate] = useState(false);
  const [hasDistrict, setHasDistrict] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ label: "", points: 5, is_active: true });

  const load = useCallback(async () => {
    const { data } = await api.get("/coach/achievement-options");
    setItems(data.items); setCanCreate(data.can_create); setHasDistrict(data.has_district_options);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.label) { toast.error("Label is required"); return; }
    try { await api.post("/coach/achievement-options", { ...form, points: parseInt(form.points, 10) }); toast.success("Created"); setOpen(false); setForm({ label: "", points: 5, is_active: true }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const updatePoints = async (o, points) => { try { await api.put(`/coach/achievement-options/${o.id}`, { points: parseInt(points, 10) }); toast.success("Updated"); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const toggle = async (o) => { try { await api.put(`/coach/achievement-options/${o.id}`, { is_active: !o.is_active }); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const remove = async (o) => { try { await api.delete(`/coach/achievement-options/${o.id}`); toast.success("Deleted"); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };

  const mine = (o) => o.created_by === "coach" && o.team_id;

  return (
    <div>
      <button onClick={() => navigate("/coach/lettering")} className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-slate-800" data-testid="back-to-lettering"><ArrowLeft className="h-4 w-4" /> Back to Letter Points Review</button>
      <PageHeader title="Achievement Options" subtitle="Lettering point standards your team members can submit against" />

      <LetterPolicyCard getUrl="/coach/letter-policy" putUrl="/coach/letter-policy" editable />

      <div className="mb-4 flex items-start gap-2 rounded-lg bg-sky-50 p-3 text-sm text-sky-800" data-testid="coach-achievement-note">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        {hasDistrict
          ? "Your district's achievement options are set by the administrator, so they can't be edited here."
          : "Your district hasn't set achievement options yet, so you can create your own for your team below."}
      </div>

      {canCreate && (
        <div className="mb-3 flex justify-end">
          <Button onClick={() => setOpen(true)} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="coach-new-achievement-btn"><Plus className="mr-2 h-4 w-4" /> Add option</Button>
        </div>
      )}

      {items.length === 0 ? <EmptyState title="No options yet" description={canCreate ? "Add achievement options for your team." : "The administrator hasn't added options for your team type yet."} testId="coach-achievements-empty" /> : (
        <Card className="border-slate-200 shadow-sm overflow-x-auto">
          <Table data-testid="coach-achievements-table">
            <TableHeader><TableRow className="bg-slate-50">{["Achievement", "Points", "Source", "Active", ""].map((h) => <TableHead key={h} className="text-xs font-bold uppercase tracking-wide text-slate-500">{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {items.map((o) => (
                <TableRow key={o.id} data-testid={`coach-achievement-row-${o.id}`}>
                  <TableCell className="font-medium text-slate-800">{o.label}</TableCell>
                  <TableCell>
                    {mine(o)
                      ? <Input type="number" defaultValue={o.points} className="h-8 w-20 tabular-nums" onBlur={(e) => Number(e.target.value) !== o.points && updatePoints(o, e.target.value)} data-testid={`coach-achievement-points-${o.id}`} />
                      : <span className="tabular-nums">{o.points}</span>}
                  </TableCell>
                  <TableCell><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${mine(o) ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{mine(o) ? "My team" : "District"}</span></TableCell>
                  <TableCell>{mine(o) ? <Switch checked={o.is_active} onCheckedChange={() => toggle(o)} data-testid={`coach-achievement-toggle-${o.id}`} /> : <span className="text-xs text-slate-400">—</span>}</TableCell>
                  <TableCell className="text-right">{mine(o) && <Button size="icon" variant="ghost" className="text-red-500" onClick={() => remove(o)} data-testid={`coach-achievement-delete-${o.id}`}><Trash2 className="h-4 w-4" /></Button>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto" data-testid="coach-achievement-dialog">
          <DialogHeader><DialogTitle className="font-heading">Add achievement option</DialogTitle><DialogDescription>This option applies only to your team.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Label</Label><Input value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} data-testid="coach-achievement-label-input" /></div>
            <div className="space-y-2"><Label>Points</Label><Input type="number" value={form.points} onChange={(e) => setForm((f) => ({ ...f, points: e.target.value }))} data-testid="coach-achievement-points-input" /></div>
            <div className="flex items-center gap-3"><Switch checked={form.is_active} onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))} data-testid="coach-achievement-active-switch" /><Label>Active</Label></div>
          </div>
          <DialogFooter><Button onClick={save} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="coach-achievement-save-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
